import React, { useContext, useEffect, useState } from 'react';
import { BaseModal } from '../../../components';
import {
  Box,
  Button,
  makeStyles,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
} from '@material-ui/core';
import { operationalTierMap } from './Constants.js';
import { useAndromeda } from '../../../modules/Andromeda';
import { AppContext } from '../../../modules/AppContext';

const useStyles = makeStyles((theme) => ({
  wrapper: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    textAlign: 'left',
  },
  buttonBox: {
    display: 'flex',
    justifyContent: 'space-around',
    alignItems: 'center',
    marginTop: theme.spacing(2),
  },
  titleBox: {
    width: '40%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
  },
  valueBox: {
    width: '60%',
    display: 'flex',
    alignItems: 'center',
    paddingLeft: theme.spacing(0.5),
  },
  row: {
    padding: theme.spacing(1),
    width: '100%',
    display: 'flex',
  },
  jobTable: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  tiers: {
    width: theme.spacing(30),
  },
}));

export const EditAgentModal = ({ agent, open, handleClose }) => {
  const classes = useStyles();
  const andromeda = useAndromeda();
  const { showToast } = useContext(AppContext);

  const [loading, setLoading] = useState(false);
  const [enabled, setEnabled] = useState(null);
  const [hasCaptcha, setHasCaptcha] = useState(null);
  const [allowsMultipleNumbers, setAllowsMultipleNumbers] = useState(null);
  const [operationalTier, setOperationalTier] = useState(null);
  const [loginUrl, setLoginUrl] = useState(null);

  const rows = [
    {
      title: 'Enabled',
      value: enabled,
      formatter: (val) => (
        <Switch
          color="primary"
          checked={!!val}
          name="agentEnabled"
          onChange={() => setEnabled((prev) => !prev)}
        />
      ),
    },
    {
      title: 'Has Captcha',
      value: hasCaptcha,
      formatter: (val) => (
        <Switch
          color="primary"
          checked={!!val}
          name="agentHasCaptcha"
          onChange={() => setHasCaptcha((prev) => !prev)}
        />
      ),
    },
    {
      title: 'Multiple Number Support',
      value: allowsMultipleNumbers,
      formatter: (val) => (
        <Switch
          color="primary"
          checked={!!val}
          name="agentAllowsMultipleNumbers"
          onChange={() => setAllowsMultipleNumbers((prev) => !prev)}
        />
      ),
    },
    {
      title: 'Operational Tier',
      value: operationalTier,
      formatter: (val) => (
        <Select
          id="operationalTier"
          variant="outlined"
          className={classes.tiers}
          onChange={(event) => setOperationalTier(event.target.value)}
          value={val}
        >
          {Object.keys(operationalTierMap).map((tier, idx) => (
            <MenuItem key={idx} value={tier}>
              {operationalTierMap[tier].name}
            </MenuItem>
          ))}
        </Select>
      ),
    },
    {
      title: 'Login URL',
      value: loginUrl,
      formatter: (val) => (
        <TextField
          variant="outlined"
          value={val}
          onChange={(event) => setLoginUrl(event.target.value)}
        />
      ),
    },
  ];

  const updateAgent = async () => {
    setLoading(true);
    const dto = {
      id: agent.id,
      name: agent.name,
      enabled,
      hasCaptcha,
      allowsMultipleNumbers,
      operationalTier,
      loginUrl,
    };
    const success = await andromeda.updateAgent(agent.id, dto);
    if (success)
      showToast({
        message: 'Agent successfully updated',
        type: 'success',
      });
    else
      showToast({
        message: 'Failed to update agent',
        type: 'error',
      });

    handleClose();
    setLoading(false);
  };

  useEffect(() => {
    setEnabled(agent.enabled);
    setHasCaptcha(agent.hasCaptcha);
    setAllowsMultipleNumbers(agent.allowsMultipleNumbers);
    setOperationalTier(agent.operationalTier);
    setLoginUrl(agent.loginUrl);
  }, [agent]);

  return (
    <BaseModal
      isOpen={open}
      handleClose={handleClose}
      title={`Edit ${agent.name} Agent Configuration`}
      content={
        <Box className={classes.wrapper}>
          <Box className={classes.jobTable}>
            {rows.map((row, idx) => (
              <Box className={classes.row} key={idx}>
                <Typography variant="body1" className={classes.titleBox}>
                  <strong>{row.title}:</strong>
                </Typography>
                <Typography
                  component="div"
                  className={classes.valueBox}
                  key={`${row.key}-value`}
                >
                  {row.formatter(row.value)}
                </Typography>
              </Box>
            ))}
          </Box>
          <Box>
            <Button disabled={loading} onClick={updateAgent}>
              Save
            </Button>
          </Box>
        </Box>
      }
    />
  );
};
